<!-- Beta Environment Banner -->
<!-- <div class="beta-banner" role="alert">
  <span class="beta-dot"></span>
  You are viewing Terms on the <strong>beta.angularize.dev</strong> environment.
  See
  <a
    href="https://angularize.dev/terms-of-service"
    target="_blank"
    rel="noopener noreferrer"
    >production Terms</a
  >
  for the primary platform.
</div> -->

<!-- TOP NAV -->
<!-- <nav class="topbar" role="navigation" aria-label="Terms navigation">
  <a
    class="topbar-logo"
    href="https://angularize.dev"
    rel="noopener noreferrer"
    aria-label="Angularize.dev Home"
  >
    <div class="logo-mark" aria-hidden="true">A</div>
    <span>Angularize<span class="text-accent">.dev</span></span>
  </a>
  <div class="topbar-meta">
    <span class="legal-badge">Legal Document</span>
    <span class="updated-date">Last updated: {{ lastUpdated }}</span>
  </div>
</nav> -->

<div class="page-wrap">
  <!-- HERO -->
  <header class="hero" role="banner">
    <div class="hero-bg-glow" aria-hidden="true"></div>
    <div class="hero-eyebrow" role="note">
      <span class="eyebrow-icon" aria-hidden="true">⚖️</span>
      Legal
    </div>
    <h1 class="hero-title">
      Terms of <span class="text-accent">Service</span>
    </h1>
    <p class="hero-sub">
      Please read these Terms carefully before using
      <strong>{{ platformLabel }}</strong> or any associated services. By
      accessing our platform, you agree to be legally bound by these Terms in
      their entirety.
    </p>
    <div class="hero-meta" role="list">
      <div class="meta-pill" role="listitem">
        <strong>Product:</strong> angularize.dev &amp; beta.angularize.dev
      </div>
      <div class="meta-pill" role="listitem">
        <strong>Owner:</strong> Pythrust Technologies OPC Pvt. Ltd.
      </div>
      <div class="meta-pill" role="listitem">
        <strong>Jurisdiction:</strong> Gurugram, Haryana, India
      </div>
      <div class="meta-pill" role="listitem">
        <strong>Effective:</strong> {{ lastUpdated }}
      </div>
    </div>
  </header>

  <!-- DOCUMENT LAYOUT -->
  <div class="doc-layout">
    <!-- SIDEBAR TOC -->
    <aside class="toc" aria-label="Table of Contents" role="navigation">
      <p class="toc-label">Contents</p>
      <ul class="toc-list" role="list">
        <li *ngFor="let section of tocSections" role="listitem">
          <a
            [class.active]="section.active"
            (click)="scrollTo(section.id)"
            (keydown.enter)="scrollTo(section.id)"
            tabindex="0"
            role="link"
            [attr.aria-current]="section.active ? 'location' : null"
          >
            {{ section.label }}
          </a>
        </li>
      </ul>
    </aside>

    <!-- MAIN DOCUMENT CONTENT -->
    <main class="doc-content" #docContent id="main-content" role="main">
      <!-- ── SECTION 1: INTRODUCTION ── -->
      <section class="section-block" id="s1" aria-labelledby="h1">
        <span class="section-num">Section 01</span>
        <h2 class="section-heading" id="h1">
          Introduction &amp; Agreement Overview
        </h2>
        <p>
          These Terms of Service ("<strong>Terms</strong>") constitute a legally
          binding agreement between you ("<strong>User</strong>",
          "<strong>you</strong>", or "<strong>your</strong>") and
          <strong>Pythrust Technologies OPC Private Limited</strong>, a company
          incorporated under the laws of India ("<strong>Company</strong>",
          "<strong>we</strong>", "<strong>us</strong>", or
          "<strong>our</strong>"), operating the web application at
          <strong>angularize.dev</strong> (primary platform) and its pre-release
          version at <strong>beta.angularize.dev</strong> (collectively, the
          "<strong>Platform</strong>" or "<strong>Services</strong>").
        </p>
        <p>
          By accessing, registering, or using any part of the Services —
          including the beta release — you confirm that you have:
        </p>
        <ul class="doc-list">
          <li>Read and fully understood these Terms;</li>
          <li>Agreed to be legally bound by all provisions herein;</li>
          <li>
            Consented to our
            <a
              routerLink="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              >Privacy Policy</a
            >, incorporated by reference; and
          </li>
          <li>
            Committed to comply with all applicable laws and regulations of the
            Republic of India and any jurisdiction in which you operate.
          </li>
        </ul>
        <div class="callout callout-warn" role="alert">
          <strong>⚠ Important</strong>
          If you do not agree to any part of these Terms, you must immediately
          cease all use of the Services. Continued access constitutes
          unconditional acceptance.
        </div>
        <p>
          These Terms apply equally to the primary platform
          (<strong>angularize.dev</strong>) and the beta environment
          (<strong>beta.angularize.dev</strong>). Additional disclaimers
          specific to the beta are in Section 12.
        </p>
      </section>

      <!-- ── SECTION 2: DEFINITIONS ── -->
      <section class="section-block" id="s2" aria-labelledby="h2">
        <span class="section-num">Section 02</span>
        <h2 class="section-heading" id="h2">Definitions</h2>
        <p>
          For the purposes of these Terms, the following capitalised terms have
          the meanings set out below:
        </p>
        <div class="def-grid" role="list">
          <div class="def-row" *ngFor="let d of definitions" role="listitem">
            <div class="def-term">{{ d.term }}</div>
            <div class="def-desc">{{ d.desc }}</div>
          </div>
        </div>
      </section>

      <!-- ── SECTION 3: ELIGIBILITY ── -->
      <section class="section-block" id="s3" aria-labelledby="h3">
        <span class="section-num">Section 03</span>
        <h2 class="section-heading" id="h3">
          Eligibility &amp; Age Requirements
        </h2>
        <h3 class="sub-heading">3.1 Minimum Age</h3>
        <p>
          You must be at least <strong>18 years of age</strong> to use the
          Services. By using the Platform, you represent and warrant that you
          meet this requirement.
        </p>
        <h3 class="sub-heading">3.2 Legal Capacity</h3>
        <p>
          You represent that you have the full legal capacity to enter into a
          binding contract under the Indian Contract Act, 1872, and any
          applicable laws in your jurisdiction. If accessing on behalf of an
          organisation, you warrant you are authorised to bind that
          organisation.
        </p>
        <h3 class="sub-heading">3.3 Geographic Restrictions</h3>
        <p>
          The Services are intended for use within India and by users in
          jurisdictions where such use is not prohibited by law. Users in
          jurisdictions subject to trade embargoes or export restrictions may
          not access the Platform (see Section 18).
        </p>
      </section>

      <!-- ── SECTION 4: LICENSE ── -->
      <section class="section-block" id="s4" aria-labelledby="h4">
        <span class="section-num">Section 04</span>
        <h2 class="section-heading" id="h4">License to Use the Services</h2>
        <h3 class="sub-heading">4.1 Grant of License</h3>
        <p>
          Subject to compliance with these Terms, we grant you a limited,
          personal, non-exclusive, non-transferable, non-sublicensable,
          revocable licence to access and use the Platform for its intended
          purpose of software development and related activities.
        </p>
        <h3 class="sub-heading">4.2 Free Plan</h3>
        <p>
          Free Plan access is strictly limited to personal, non-commercial use.
          Commercial use under a Free Plan constitutes a material breach of
          these Terms.
        </p>
        <h3 class="sub-heading">4.3 Paid Plans</h3>
        <p>
          Paid subscription tiers permit commercial use in accordance with the
          applicable plan as described at
          <strong>angularize.dev/pricing</strong>.
        </p>
        <h3 class="sub-heading">4.4 Restrictions</h3>
        <p>You must not, and must not permit any third party to:</p>
        <ul class="doc-list">
          <li>
            Copy, reproduce, distribute, sell, rent, sublicense, or transfer the
            Platform or any part thereof;
          </li>
          <li>Reverse-engineer, decompile, or disassemble the Platform;</li>
          <li>
            Remove, obscure, or alter any proprietary notices or branding;
          </li>
          <li>Use the Platform to build a competing product or service;</li>
          <li>Scrape, crawl, or extract data using automated means;</li>
          <li>
            Frame, mirror, or proxy the Platform on any third-party server;
          </li>
          <li>
            Circumvent usage limits, access controls, or monitoring systems;
          </li>
          <li>
            Use the Platform in any manner that impairs or damages our
            infrastructure.
          </li>
        </ul>
        <h3 class="sub-heading">4.5 Reservation of Rights</h3>
        <p>
          All rights not expressly granted are reserved by Pythrust Technologies
          OPC Private Limited. You acquire no ownership rights in the Platform.
        </p>
      </section>

      <!-- ── SECTION 5: USER ACCOUNTS ── -->
      <section class="section-block" id="s5" aria-labelledby="h5">
        <span class="section-num">Section 05</span>
        <h2 class="section-heading" id="h5">User Accounts</h2>
        <h3 class="sub-heading">5.1 Account Creation</h3>
        <p>
          To access certain features, you must register an Account with
          accurate, current, and complete information, and keep that information
          updated.
        </p>
        <h3 class="sub-heading">5.2 Account Security</h3>
        <p>
          You are solely responsible for maintaining the confidentiality of your
          login credentials and for all activities under your Account. Notify us
          immediately at
          <a href="mailto:angularize.dev@gmail.com"
            >angularize.dev&#64;gmail.com</a
          >
          if you suspect any unauthorised access.
        </p>
        <h3 class="sub-heading">5.3 One Account per User</h3>
        <p>
          Each User may maintain only one Account. Creating multiple Accounts to
          circumvent usage limits or for fraudulent purposes is strictly
          prohibited and may result in immediate termination.
        </p>
        <h3 class="sub-heading">5.4 Account Deletion</h3>
        <p>
          Request deletion by emailing
          <a href="mailto:angularize.dev@gmail.com"
            >angularize.dev&#64;gmail.com</a
          >. Upon deletion: access terminates immediately; User Content is
          deleted within <strong>90 days</strong>; active subscriptions are
          non-refundable for unused portions.
        </p>
      </section>

      <!-- ── SECTION 6: SUBSCRIPTIONS ── -->
      <section class="section-block" id="s6" aria-labelledby="h6">
        <span class="section-num">Section 06</span>
        <h2 class="section-heading" id="h6">
          Subscriptions, Pricing &amp; Payments
        </h2>
        <h3 class="sub-heading">6.1 Subscription Plans</h3>
        <p>
          The Platform is offered under various plans with differing features
          and quotas. Current pricing is at
          <strong>angularize.dev/pricing</strong>. We reserve the right to
          modify plans with reasonable prior notice.
        </p>
        <h3 class="sub-heading">6.2 Recurring Billing</h3>
        <p>
          Paid Subscriptions are billed on a recurring monthly or annual basis
          and renew automatically unless cancelled before the renewal date. By
          subscribing, you authorise us or our payment processor to charge your
          chosen payment method for all applicable fees.
        </p>
        <h3 class="sub-heading">6.3 Refund Policy</h3>
        <div class="callout callout-warn" role="alert">
          <strong>⚠ Non-Refundable by Default</strong>
          All payments are non-refundable unless otherwise determined at our
          sole discretion.
        </div>
        <p>
          Refunds may be considered for: demonstrable billing errors or
          duplicate charges; platform outages exceeding 72 continuous hours
          solely attributable to us; or other circumstances at our absolute
          discretion. Where approved, refunds are processed within
          <strong>14 business days</strong>.
        </p>
        <h3 class="sub-heading">6.4 Cancellation</h3>
        <p>
          Cancel at any time via account billing settings. Access remains active
          until period end; subscriptions do not renew; unused credits expire
          without refund.
        </p>
        <h3 class="sub-heading">6.5 Taxes</h3>
        <p>
          All prices are exclusive of applicable taxes including GST unless
          stated otherwise. You bear all applicable taxes in your jurisdiction.
        </p>
      </section>

      <!-- ── SECTION 7: ACCEPTABLE USE ── -->
      <section class="section-block" id="s7" aria-labelledby="h7">
        <span class="section-num">Section 07</span>
        <h2 class="section-heading" id="h7">Acceptable Use Policy</h2>
        <p>
          Violations of this policy may result in immediate Account suspension
          or termination without refund.
        </p>
        <h3 class="sub-heading">7.1 Prohibited Conduct</h3>
        <p>You must not use the Services to:</p>
        <ul class="doc-list">
          <li>
            Violate any applicable law or regulation, including the Information
            Technology Act, 2000 (India) and its amendments;
          </li>
          <li>
            Infringe or misappropriate intellectual property rights of any third
            party;
          </li>
          <li>
            Transmit, upload, or distribute malware, viruses, ransomware,
            spyware, or harmful code of any kind;
          </li>
          <li>
            Conduct or facilitate unauthorised penetration testing,
            vulnerability scanning, or security assessments of any systems;
          </li>
          <li>
            Interfere with or disrupt the integrity or performance of the
            Services or connected networks;
          </li>
          <li>
            Gain or attempt to gain unauthorised access to any systems,
            networks, or data;
          </li>
          <li>
            Engage in phishing, social engineering, impersonation, or identity
            fraud;
          </li>
          <li>
            Generate, distribute, or publish spam or unsolicited bulk
            communications;
          </li>
          <li>Promote, facilitate, or assist in any illegal activity;</li>
          <li>
            Engage in discriminatory, defamatory, abusive, threatening, or
            harassing conduct;
          </li>
          <li>
            Publish pornographic, obscene, or indecent content in violation of
            applicable Indian law;
          </li>
          <li>Violate any individual's privacy or data protection rights;</li>
          <li>
            Misrepresent your identity or affiliation with any person or entity.
          </li>
        </ul>
        <h3 class="sub-heading">7.2 Resource Abuse</h3>
        <p>
          You must not exceed your Plan's resource quotas. Excessive usage
          degrading Platform performance for others may result in throttling,
          suspension, or termination without prior notice.
        </p>
        <h3 class="sub-heading">7.3 Reporting Misuse</h3>
        <p>
          Report misuse or suspicious activity immediately to
          <a href="mailto:angularize.dev@gmail.com"
            >angularize.dev&#64;gmail.com</a
          >
          with a full description of the incident.
        </p>
      </section>

      <!-- ── SECTION 8: AI FEATURES ── -->
      <section class="section-block" id="s8" aria-labelledby="h8">
        <span class="section-num">Section 08</span>
        <h2 class="section-heading" id="h8">
          AI Features, Code Generation &amp; Generated Output
        </h2>
        <h3 class="sub-heading">8.1 Nature of AI Services</h3>
        <p>
          Angularize.dev uses artificial intelligence and large language models
          to assist with Angular application development, component generation,
          and code scaffolding. AI systems are probabilistic and do not
          guarantee accuracy, correctness, or fitness for purpose.
        </p>
        <h3 class="sub-heading">8.2 Ownership of Generated Output</h3>
        <p>
          Subject to these Terms and applicable third-party open-source
          licences, you retain ownership of code and applications developed
          using the Platform, including Generated Outputs. The Company makes no
          claim of ownership over your applications or derived works.
        </p>
        <h3 class="sub-heading">8.3 Your Responsibility to Review</h3>
        <div class="callout callout-warn" role="alert">
          <strong>⚠ Critical</strong>
          All Generated Output must be reviewed, tested, and validated before
          deployment in any production environment. You assume all risks
          associated with using Generated Output.
        </div>
        <ul class="doc-list">
          <li>
            The Company disclaims all liability for Generated Output containing
            bugs, security vulnerabilities, or errors;
          </li>
          <li>
            Generated code may reference third-party dependencies with their own
            licences — you are responsible for compliance;
          </li>
          <li>
            Generated Output may become outdated or incompatible with future
            framework versions.
          </li>
        </ul>
        <h3 class="sub-heading">8.4 AI Training Data</h3>
        <p>
          By default, the Company does not use your proprietary User Content to
          train AI models without your explicit consent. Aggregate, anonymised
          usage patterns may be used to improve Platform capabilities.
        </p>
        <h3 class="sub-heading">8.5 AI Bias Disclaimer</h3>
        <p>
          AI systems may produce outputs reflecting biases present in training
          data. Review all outputs through your own technical and ethical lens
          before use.
        </p>
      </section>

      <!-- ── SECTION 9: INTELLECTUAL PROPERTY ── -->
      <section class="section-block" id="s9" aria-labelledby="h9">
        <span class="section-num">Section 09</span>
        <h2 class="section-heading" id="h9">Intellectual Property Rights</h2>
        <h3 class="sub-heading">9.1 Company's Intellectual Property</h3>
        <p>
          Pythrust Technologies OPC Private Limited exclusively owns all rights,
          title, and interest in and to:
        </p>
        <ul class="doc-list">
          <li>
            The "<strong>Angularize</strong>" name, brand, logos, and visual
            identity;
          </li>
          <li>
            The domains <strong>angularize.dev</strong> and
            <strong>beta.angularize.dev</strong>;
          </li>
          <li>
            The Platform's architecture, design, UI/UX, algorithms, and
            codebase;
          </li>
          <li>
            All documentation, supporting materials, and Company-created
            training data;
          </li>
          <li>
            All improvements, modifications, and derivative works created by or
            for the Company.
          </li>
        </ul>
        <p>
          Protected under the Copyright Act, 1957 (India), Trade Marks Act, 1999
          (India), and other applicable intellectual property laws.
        </p>
        <h3 class="sub-heading">9.2 User Content Licence</h3>
        <p>
          You retain full ownership of User Content. By submitting it to the
          Platform, you grant us a limited, non-exclusive, worldwide,
          royalty-free licence to process it solely to provide and operate the
          Services. We may not sell, sublicense, or distribute your User Content
          externally.
        </p>
        <h3 class="sub-heading">9.3 Feedback Licence</h3>
        <p>
          Feedback, suggestions, and ideas you provide grant us a perpetual,
          irrevocable, royalty-free, worldwide licence to use and implement
          without compensation or attribution.
        </p>
        <h3 class="sub-heading">9.4 Third-Party Intellectual Property</h3>
        <p>
          The Platform may integrate or generate code referencing third-party
          frameworks (including Angular, Angular Material, etc.), all subject to
          their own licences. You are solely responsible for licence compliance.
        </p>
      </section>

      <!-- ── SECTION 10: DATA & PRIVACY ── -->
      <section class="section-block" id="s10" aria-labelledby="h10">
        <span class="section-num">Section 10</span>
        <h2 class="section-heading" id="h10">Data, Privacy &amp; Security</h2>
        <h3 class="sub-heading">10.1 Privacy Policy</h3>
        <p>
          Our
          <a
            routerLink="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            >Privacy Policy</a
          >
          governs collection, storage, and use of your data and is incorporated
          into these Terms by reference.
        </p>
        <h3 class="sub-heading">10.2 Data Security</h3>
        <p>
          We implement commercially reasonable security measures including TLS
          encryption, access controls, and regular security assessments. No
          electronic storage system is 100% secure; absolute security cannot be
          guaranteed.
        </p>
        <h3 class="sub-heading">10.3 Data Retention</h3>
        <p>
          We retain Account and User Content data for the duration of your
          active Account. User Content is removed within 90 days of account
          deletion. Certain data may be retained longer as required by law,
          accounting obligations, or dispute resolution.
        </p>
        <h3 class="sub-heading">10.4 Processing Third-Party Personal Data</h3>
        <p>
          If you process personal data of third parties via the Platform, you
          must: obtain all necessary consents under applicable law including the
          Digital Personal Data Protection Act, 2023 (India); ensure processing
          complies with applicable laws; and not process sensitive data without
          appropriate safeguards.
        </p>
        <h3 class="sub-heading">10.5 Data Backups</h3>
        <p>
          You are solely responsible for maintaining your own backups. While we
          implement backup procedures, we do not guarantee against data loss or
          corruption.
        </p>
      </section>

      <!-- ── SECTION 11: CYBER SECURITY ── -->
      <section class="section-block" id="s11" aria-labelledby="h11">
        <span class="section-num">Section 11</span>
        <h2 class="section-heading" id="h11">
          Cyber Security &amp; Protection Against Attacks
        </h2>
        <div class="callout callout-info" role="note">
          <strong>🔐 Security Notice</strong>
          Pythrust Technologies OPC Private Limited takes cyber security
          extremely seriously. This section details both our protections and
          your obligations.
        </div>
        <h3 class="sub-heading">11.1 Prohibited Cyber Activities</h3>
        <p>
          The following constitute serious violations of these Terms and may
          constitute criminal offences under the Information Technology Act,
          2000 (India) and its amendments, including the IT (Amendment) Act,
          2008:
        </p>
        <ul class="doc-list">
          <li>
            Any unauthorised access to or use of the Platform's systems,
            servers, networks, or databases (Section 66, IT Act);
          </li>
          <li>
            Denial of service (DoS) or distributed denial of service (DDoS)
            attacks against the Platform or its infrastructure;
          </li>
          <li>
            SQL injection, cross-site scripting (XSS), cross-site request
            forgery (CSRF), or any injection-based attacks;
          </li>
          <li>
            Man-in-the-middle (MITM) attacks, packet sniffing, or session
            hijacking;
          </li>
          <li>
            Brute-force or credential stuffing attacks against authentication
            systems;
          </li>
          <li>
            Exploitation of zero-day vulnerabilities or known vulnerabilities in
            the Platform;
          </li>
          <li>
            Deployment of bots, crawlers, or automated scripts to abuse or
            overload the Platform;
          </li>
          <li>
            Attempting to extract, copy, or exfiltrate proprietary data, source
            code, or user data;
          </li>
          <li>
            Installing or distributing any malware, trojans, ransomware,
            spyware, or keyloggers;
          </li>
          <li>
            Conducting social engineering, phishing, or pretexting attacks
            targeting our users or staff;
          </li>
          <li>
            Bypassing or disabling any security mechanism, rate limit, CAPTCHA,
            or authentication control;
          </li>
          <li>
            Creating or using fake identities to gain unauthorised access;
          </li>
          <li>
            Exploiting API endpoints beyond their intended purpose or in
            violation of documented limits.
          </li>
        </ul>
        <h3 class="sub-heading">11.2 Consequences of Cyber Attacks</h3>
        <p>Any violation of Section 11.1 will result in:</p>
        <ul class="doc-list">
          <li>
            Immediate termination of your Account without notice or refund;
          </li>
          <li>
            Reporting to relevant law enforcement agencies, including the Indian
            Computer Emergency Response Team (CERT-In), the Cyber Crime Cell,
            and other applicable authorities;
          </li>
          <li>
            Civil claims for damages, including direct, indirect, consequential,
            and exemplary damages arising from the attack;
          </li>
          <li>
            Criminal prosecution under the IT Act, 2000, IPC (Indian Penal
            Code), and any other applicable legislation;
          </li>
          <li>Injunctive relief to prevent further harm.</li>
        </ul>
        <h3 class="sub-heading">11.3 Security Measures We Implement</h3>
        <p>We employ the following protections on an ongoing basis:</p>
        <ul class="doc-list">
          <li>TLS/SSL encryption for all data in transit;</li>
          <li>Encryption at rest for sensitive data;</li>
          <li>Role-based access control (RBAC) for internal systems;</li>
          <li>Web Application Firewall (WAF) and DDoS mitigation;</li>
          <li>Rate limiting and API throttling on all endpoints;</li>
          <li>Periodic third-party security audits and penetration tests;</li>
          <li>Real-time monitoring, intrusion detection, and alerting;</li>
          <li>Secure software development lifecycle (SSDLC) practices;</li>
          <li>Multi-factor authentication (MFA) for administrative access.</li>
        </ul>
        <p>
          Despite these measures, no security is absolute. We cannot guarantee
          the Platform will never be subject to security incidents.
        </p>
        <h3 class="sub-heading">11.4 Responsible Disclosure</h3>
        <p>
          If you discover a security vulnerability in the Platform, you must
          report it responsibly to
          <a href="mailto:angularize.dev@gmail.com"
            >angularize.dev&#64;gmail.com</a
          >
          before any public disclosure, giving us a minimum of
          <strong>90 days</strong> to investigate and remediate. We will not
          pursue legal action against good-faith security researchers who follow
          this responsible disclosure protocol. Exploitation of discovered
          vulnerabilities, however minor, will be treated as a material breach
          of these Terms.
        </p>
        <h3 class="sub-heading">11.5 Your Security Obligations</h3>
        <p>You are obligated to:</p>
        <ul class="doc-list">
          <li>Use strong, unique passwords and enable MFA where available;</li>
          <li>Not share your credentials with any third party;</li>
          <li>Promptly report any suspicious activity on your Account;</li>
          <li>
            Keep your own systems and software patched and secure when accessing
            the Platform;
          </li>
          <li>
            Not attempt to probe, scan, or test the vulnerability of the
            Platform's systems.
          </li>
        </ul>
        <h3 class="sub-heading">11.6 Incident Notification</h3>
        <p>
          In the event of a security incident affecting your data, we will
          notify you in accordance with applicable law, including the Digital
          Personal Data Protection Act, 2023 (India) and the IT (Reasonable
          Security Practices) Rules, 2011.
        </p>
      </section>

      <!-- ── SECTION 12: BETA VERSION ── -->
      <section class="section-block" id="s12" aria-labelledby="h12">
        <span class="section-num">Section 12</span>
        <h2 class="section-heading" id="h12">
          Beta Version — Additional Terms
        </h2>
        <div class="callout callout-info" role="note">
          <strong>ℹ Beta Notice</strong>
          These provisions apply specifically to
          <strong>beta.angularize.dev</strong> in addition to all other
          applicable sections of these Terms.
        </div>
        <h3 class="sub-heading">12.1 Pre-Release Status</h3>
        <p>
          The Beta Version is a pre-release product provided for testing and
          evaluation. It may be incomplete, contain errors, and differ
          substantially from the primary production platform at
          <strong>angularize.dev</strong>. Features may be modified or removed
          without notice.
        </p>
        <h3 class="sub-heading">12.2 No Warranty for Beta</h3>
        <p>
          The Beta Version is provided strictly "AS IS" with no representations
          or warranties of any kind. We explicitly disclaim all warranties of
          performance, reliability, or fitness for purpose for the Beta.
        </p>
        <h3 class="sub-heading">12.3 Data Risk in Beta</h3>
        <div class="callout callout-warn" role="alert">
          <strong>⚠ Warning</strong>
          Data stored in the Beta environment may be lost, corrupted, or reset
          without notice. Do not store production-critical or sensitive data in
          beta.angularize.dev.
        </div>
        <h3 class="sub-heading">12.4 Feedback Obligation</h3>
        <p>
          Beta users are encouraged to report bugs and provide feedback to
          <a href="mailto:angularize.dev@gmail.com"
            >angularize.dev&#64;gmail.com</a
          >. All feedback is subject to Section 9.3.
        </p>
        <h3 class="sub-heading">12.5 Beta Termination</h3>
        <p>
          We reserve the right to discontinue, modify, or transition the Beta
          Version to production at any time without liability or obligation to
          provide advance notice or continued access.
        </p>
      </section>

      <!-- ── SECTION 13: DISCLAIMER ── -->
      <section class="section-block" id="s13" aria-labelledby="h13">
        <span class="section-num">Section 13</span>
        <h2 class="section-heading" id="h13">Disclaimer of Warranties</h2>
        <div class="legal-caps" role="note">
          YOUR USE OF THE SERVICES IS ENTIRELY AT YOUR OWN RISK. THE SERVICES
          ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY GUARANTEES OR
          WARRANTIES OF ANY KIND. TO THE FULLEST EXTENT PERMITTED BY THE LAWS OF
          INDIA AND OTHER APPLICABLE JURISDICTIONS, PYTHRUST TECHNOLOGIES OPC
          PRIVATE LIMITED AND ITS OFFICERS, DIRECTORS, EMPLOYEES, PARTNERS,
          AGENTS, SUPPLIERS, AND LICENSORS HEREBY EXPRESSLY DISCLAIM ALL
          WARRANTIES — WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE —
          INCLUDING BUT NOT LIMITED TO: (A) IMPLIED WARRANTIES OF
          MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
          NON-INFRINGEMENT; (B) WARRANTIES THAT THE SERVICES WILL BE
          UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE; (C) WARRANTIES REGARDING
          ACCURACY, RELIABILITY, OR USEFULNESS OF ANY GENERATED OUTPUT; (D)
          WARRANTIES THAT THE SERVICES WILL MEET YOUR TECHNICAL OR BUSINESS
          REQUIREMENTS; (E) WARRANTIES REGARDING COMPATIBILITY OF GENERATED CODE
          WITH THIRD-PARTY SYSTEMS. THIS DISCLAIMER APPLIES TO BOTH
          ANGULARIZE.DEV AND BETA.ANGULARIZE.DEV.
        </div>
      </section>

      <!-- ── SECTION 14: LIABILITY ── -->
      <section class="section-block" id="s14" aria-labelledby="h14">
        <span class="section-num">Section 14</span>
        <h2 class="section-heading" id="h14">Limitation of Liability</h2>
        <div class="legal-caps" role="note">
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL
          PYTHRUST TECHNOLOGIES OPC PRIVATE LIMITED, ITS AFFILIATES, DIRECTORS,
          OFFICERS, EMPLOYEES, AGENTS, OR LICENSORS BE LIABLE FOR ANY: (A)
          INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY
          DAMAGES; (B) LOSS OF PROFITS, REVENUE, GOODWILL, DATA, OR BUSINESS
          OPPORTUNITIES; (C) LOSSES ARISING FROM RELIANCE ON GENERATED OUTPUT IN
          PRODUCTION; (D) LOSSES ARISING FROM SECURITY VULNERABILITIES IN
          GENERATED CODE; (E) LOSSES ARISING FROM THIRD-PARTY INCOMPATIBILITY OR
          DEPENDENCY CHANGES; (F) LOSSES ARISING FROM PROJECT DELAYS, TECHNICAL
          DEBT, OR ADDITIONAL DEVELOPMENT COSTS; (G) LOSSES FROM DATA
          CORRUPTION, LOSS, OR UNAUTHORISED ACCESS; WHETHER BASED ON CONTRACT,
          TORT, STATUTE, OR ANY OTHER LEGAL THEORY, EVEN IF ADVISED OF THE
          POSSIBILITY OF SUCH DAMAGES.
        </div>
        <h3 class="sub-heading">14.1 Aggregate Liability Cap</h3>
        <p>
          Our total aggregate liability for all claims shall not exceed the
          greater of: (a) the total amounts paid by you in the
          <strong>twelve (12) months</strong> preceding the event; or (b)
          <strong>₹5,000 (Indian Rupees Five Thousand)</strong>, whichever is
          greater.
        </p>
        <div class="callout callout-neutral" role="note">
          <strong>📌 Note</strong>
          Some jurisdictions do not allow certain exclusions or limitations. In
          such cases, exclusions apply to the fullest extent permitted by local
          law.
        </div>
      </section>

      <!-- ── SECTION 15: INDEMNIFICATION ── -->
      <section class="section-block" id="s15" aria-labelledby="h15">
        <span class="section-num">Section 15</span>
        <h2 class="section-heading" id="h15">Indemnification</h2>
        <p>
          You agree to defend, indemnify, and hold harmless Pythrust
          Technologies OPC Private Limited and its affiliates, directors,
          officers, employees, agents, successors, and assigns from and against
          all claims, liabilities, damages, losses, penalties, costs, and
          expenses (including reasonable legal fees) arising from:
        </p>
        <ul class="doc-list">
          <li>Your use of or access to the Services;</li>
          <li>Your User Content;</li>
          <li>Your breach of any provision of these Terms;</li>
          <li>
            Your violation of any applicable law, regulation, or third-party
            rights;
          </li>
          <li>
            Any application, product, or service you develop using the Platform;
          </li>
          <li>
            Any cyber attack, misuse, or security breach attributable to you or
            your systems;
          </li>
          <li>
            Any misrepresentation made by you in connection with the Services;
          </li>
          <li>
            Any third-party claim that your User Content infringes their
            intellectual property rights.
          </li>
        </ul>
      </section>

      <!-- ── SECTION 16: TERMINATION ── -->
      <section class="section-block" id="s16" aria-labelledby="h16">
        <span class="section-num">Section 16</span>
        <h2 class="section-heading" id="h16">Term &amp; Termination</h2>
        <h3 class="sub-heading">16.1 Term</h3>
        <p>
          These Terms take effect when you first access or use the Services and
          continue until terminated.
        </p>
        <h3 class="sub-heading">16.2 Termination by You</h3>
        <p>
          Terminate your Account at any time by requesting deletion via
          <a href="mailto:angularize.dev@gmail.com"
            >angularize.dev&#64;gmail.com</a
          >
          or cancelling via account settings.
        </p>
        <h3 class="sub-heading">16.3 Termination by Us</h3>
        <p>
          We may suspend or terminate your Account at any time, with or without
          notice, if you: breach these Terms; fail to make payment; cause harm
          to other users or the Platform; are the subject of a regulatory
          requirement; or if we discontinue the Services.
        </p>
        <h3 class="sub-heading">16.4 Effect of Termination</h3>
        <p>
          Upon termination: your licence ceases; you must stop all use; you lose
          access to your Account and data; User Content is deleted within 90
          days; no refunds are issued for prepaid fees; and survival provisions
          take effect.
        </p>
        <h3 class="sub-heading">16.5 Survival</h3>
        <p>
          Sections covering Definitions, Intellectual Property, Cyber Security,
          Disclaimer of Warranties, Limitation of Liability, Indemnification,
          Governing Law, and Miscellaneous survive termination.
        </p>
      </section>

      <!-- ── SECTION 17: GOVERNING LAW ── -->
      <section class="section-block" id="s17" aria-labelledby="h17">
        <span class="section-num">Section 17</span>
        <h2 class="section-heading" id="h17">
          Governing Law &amp; Dispute Resolution
        </h2>
        <h3 class="sub-heading">17.1 Governing Law</h3>
        <p>
          These Terms shall be governed by and construed in accordance with the
          laws of the <strong>Republic of India</strong>, without regard to
          conflict of law principles.
        </p>
        <h3 class="sub-heading">17.2 Exclusive Jurisdiction</h3>
        <div class="callout callout-warn" role="alert">
          <strong>⚖ Jurisdiction</strong>
          Any legal action or proceeding arising out of or related to these
          Terms or your use of the Services shall be brought exclusively before
          the competent courts of <strong>Gurugram, Haryana, India</strong>. You
          irrevocably consent to and submit to the personal jurisdiction of such
          courts.
        </div>
        <h3 class="sub-heading">17.3 Pre-Litigation Notice</h3>
        <p>
          Before initiating legal proceedings, the disputing party must send a
          written Notice of Dispute describing the claim and relief sought.
          Parties have <strong>30 days</strong> from receipt to resolve the
          dispute in good faith.
        </p>
        <div
          class="contact-card"
          role="group"
          aria-label="Legal notice address"
        >
          <div class="contact-item">
            <span class="lbl">Company</span>Pythrust Technologies OPC Private
            Limited
          </div>
          <div class="contact-item">
            <span class="lbl">Address</span>WeWork, 5th Floor, DLF Two Horizon
            Centre,<br />Harizan Colony, DLF Phase 5, Sector 43,<br />Gurugram,
            Haryana 122002, India
          </div>
          <div class="contact-item">
            <span class="lbl">Email</span
            ><a href="mailto:angularize.dev@gmail.com"
              >angularize.dev&#64;gmail.com</a
            >
          </div>
        </div>
        <h3 class="sub-heading">17.4 Injunctive Relief</h3>
        <p>
          Either party may seek interim or injunctive relief from a competent
          court to prevent irreparable harm without the pre-litigation notice
          requirement.
        </p>
      </section>

      <!-- ── SECTION 18: EXPORT CONTROLS ── -->
      <section class="section-block" id="s18" aria-labelledby="h18">
        <span class="section-num">Section 18</span>
        <h2 class="section-heading" id="h18">
          Export Controls &amp; Sanctions Compliance
        </h2>
        <p>
          The Services must not be used in violation of export control laws,
          trade sanctions, or embargoes enacted by India, the United Nations,
          the United States, the European Union, or any other relevant
          authority.
        </p>
        <p>By using the Services, you represent and warrant that:</p>
        <ul class="doc-list">
          <li>
            You are not located in or a national of any country subject to
            applicable trade sanctions;
          </li>
          <li>
            You are not listed on any restricted, denied, or sanctioned persons
            list;
          </li>
          <li>
            You will not use the Services to develop products subject to export
            restrictions;
          </li>
          <li>
            You will not re-export or transfer any part of the Services in
            violation of applicable law.
          </li>
        </ul>
        <p>
          Violation of this Section constitutes an immediate material breach and
          may be reported to relevant regulatory authorities.
        </p>
      </section>

      <!-- ── SECTION 19: COPYRIGHT ── -->
      <section class="section-block" id="s19" aria-labelledby="h19">
        <span class="section-num">Section 19</span>
        <h2 class="section-heading" id="h19">Copyright Policy</h2>
        <h3 class="sub-heading">19.1 Infringement Notice</h3>
        <p>
          If you believe content on the Platform infringes your copyright,
          submit a written notice to
          <a href="mailto:angularize.dev@gmail.com"
            >angularize.dev&#64;gmail.com</a
          >
          including: your signature; identification of the infringed work;
          location of the infringing material; your contact information; a
          good-faith belief statement; and a statement of accuracy under penalty
          of perjury.
        </p>
        <h3 class="sub-heading">19.2 Repeat Infringers</h3>
        <p>
          We will terminate Accounts of Users who are repeat infringers of
          intellectual property rights.
        </p>
        <h3 class="sub-heading">19.3 Counter-Notice</h3>
        <p>
          If content was removed due to a mistaken claim, submit a
          counter-notice to
          <a href="mailto:angularize.dev@gmail.com"
            >angularize.dev&#64;gmail.com</a
          >
          with information required under applicable law.
        </p>
      </section>

      <!-- ── SECTION 20: MODIFICATIONS ── -->
      <section class="section-block" id="s20" aria-labelledby="h20">
        <span class="section-num">Section 20</span>
        <h2 class="section-heading" id="h20">Modifications to Terms</h2>
        <p>
          We reserve the right to modify these Terms at any time. For material
          changes, we will provide at least
          <strong>30 days' notice</strong> via: posting revised Terms at
          <strong>angularize.dev/terms-of-service</strong> with an updated date;
          sending a notification to your Account email; and/or displaying a
          prominent notice within the Platform.
        </p>
        <p>
          Continued use after changes take effect constitutes acceptance. If you
          disagree, you must immediately cease using the Services.
        </p>
      </section>

      <!-- ── SECTION 21: MISCELLANEOUS ── -->
      <section class="section-block" id="s21" aria-labelledby="h21">
        <span class="section-num">Section 21</span>
        <h2 class="section-heading" id="h21">Miscellaneous</h2>
        <h3 class="sub-heading">21.1 Entire Agreement</h3>
        <p>
          These Terms, together with the Privacy Policy and any other legal
          notices published on the Platform, constitute the entire agreement
          between you and the Company regarding use of the Services.
        </p>
        <h3 class="sub-heading">21.2 Severability</h3>
        <p>
          If any provision is held invalid or unenforceable, it shall be
          modified to the minimum extent necessary, and the remaining provisions
          continue in full force.
        </p>
        <h3 class="sub-heading">21.3 Waiver</h3>
        <p>
          Failure to enforce any right shall not constitute a waiver of that
          right. Any waiver must be in writing signed by an authorised
          representative.
        </p>
        <h3 class="sub-heading">21.4 Assignment</h3>
        <p>
          You may not assign your rights or obligations under these Terms
          without our prior written consent. We may freely assign these Terms.
        </p>
        <h3 class="sub-heading">21.5 Force Majeure</h3>
        <p>
          We shall not be liable for failures resulting from causes beyond our
          reasonable control, including acts of God, natural disasters,
          pandemic, war, government actions, internet infrastructure failures,
          or cyber attacks by third parties on our infrastructure.
        </p>
        <h3 class="sub-heading">21.6 No Agency</h3>
        <p>
          Nothing in these Terms creates a partnership, joint venture,
          employment, or agency relationship between you and the Company.
        </p>
        <h3 class="sub-heading">21.7 Language</h3>
        <p>
          These Terms are in English. In any conflict between an English version
          and a translation, the English version prevails.
        </p>
      </section>

      <!-- ── SECTION 22: CONTACT ── -->
      <section class="section-block" id="s22" aria-labelledby="h22">
        <span class="section-num">Section 22</span>
        <h2 class="section-heading" id="h22">Contact Information</h2>
        <p>For any questions, concerns, or requests relating to these Terms:</p>
        <div class="contact-card" role="group" aria-label="Contact details">
          <div class="contact-item">
            <span class="lbl">Company</span>
            Pythrust Technologies OPC Private Limited
          </div>
          <div class="contact-item">
            <span class="lbl">Product</span>
            Angularize.dev
          </div>
          <div class="contact-item">
            <span class="lbl">Email</span>
            <a href="mailto:angularize.dev@gmail.com"
              >angularize.dev&#64;gmail.com</a
            >
          </div>
          <div class="contact-item">
            <span class="lbl">Address</span>
            WeWork, 5th Floor, DLF Two Horizon Centre,<br />
            Harizan Colony, DLF Phase 5, Sector 43,<br />
            Gurugram, Haryana 122002, India
          </div>
          <div class="contact-item">
            <span class="lbl">Platforms</span>
            <a
              href="https://angularize.dev"
              target="_blank"
              rel="noopener noreferrer"
              >angularize.dev</a
            >
            &nbsp;&amp;&nbsp;
            <a
              href="https://beta.angularize.dev"
              target="_blank"
              rel="noopener noreferrer"
              >beta.angularize.dev</a
            >
          </div>
        </div>
      </section>
    </main>
    <!-- /doc-content -->
  </div>
  <!-- /doc-layout -->

  <!-- FOOTER -->
  <!-- <footer class="doc-footer" role="contentinfo">
    <p class="footer-brand">Angularize<span class="text-accent">.dev</span></p>
    <p class="footer-copy">
      © {{ lastUpdated.split(" ")[2] }} Pythrust Technologies OPC Private
      Limited. All Rights Reserved.
    </p>
    <nav class="footer-links" aria-label="Footer navigation">
      <a href="https://angularize.dev" target="_blank" rel="noopener noreferrer"
        >Home</a
      >
      <a
        href="https://angularize.dev/privacy-policy"
        target="_blank"
        rel="noopener noreferrer"
        >Privacy Policy</a
      >
      <a href="mailto:angularize.dev@gmail.com">Contact</a>
      <a
        href="https://beta.angularize.dev"
        target="_blank"
        rel="noopener noreferrer"
        >Beta Platform</a
      >
    </nav>
  </footer> -->
</div>
<!-- /page-wrap -->
